import IconButton from '@mui/material/IconButton';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { alpha, Theme } from '@mui/material/styles';
import { FavoriteEntity, useIsFavorite, useToggleFavorite } from '../../utils';

function getBackgroundColor(theme: Theme, active: boolean): string {
  if (active) {
    const isLight = theme.palette.mode === 'light';
    return isLight
      ? alpha(theme.palette.common.black, 0.34)
      : alpha(theme.palette.warning.main, 0.12);
  }

  return 'action.selected';
}

function getHoverBackgroundColor(theme: Theme, active: boolean): string {
  if (active) {
    const isLight = theme.palette.mode === 'light';
    return isLight
      ? alpha(theme.palette.common.black, 0.12)
      : alpha(theme.palette.warning.main, 0.2);
  }

  return alpha(theme.palette.warning.main, 0.08);
}

type Props = {
  readonly item: FavoriteEntity;
  readonly label?: string;
  readonly variant?: 'default' | 'minimal';
};

export function FavoriteStarButton({
  item,
  label = 'Add to favorites',
  variant = 'default',
}: Props) {
  const active = useIsFavorite(item);
  const toggleFavorite = useToggleFavorite();

  return (
    <IconButton
      aria-label={active ? 'Remove from favorites' : label}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite(item);
      }}
      sx={theme => ({
        ...(variant === 'default' && {
          bgcolor: getBackgroundColor(theme, active),
        }),
        color: active ? theme.palette.warning.main : 'text.secondary',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          ...(variant === 'default' && {
            bgcolor: getHoverBackgroundColor(theme, active),
          }),
          color: theme.palette.warning.main,
          transform: variant === 'default' ? 'scale(1.05)' : 'scale(1.1)',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      })}
    >
      {active ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
    </IconButton>
  );
}
